import React from 'react'
import { motion } from 'framer-motion'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import {
  Award,
  Users,
  Leaf,
  Star,
  CheckCircle,
  ArrowRight,
  Quote,
} from 'lucide-react'
import { useNavigate } from 'react-router'
import { Card } from '../ui/card'
import { CardContent } from '../ui/card'
import { ImageWithFallback } from '../../utils/ImageWithFallback'

export function AboutComponent2() {
  const navigate = useNavigate()

  const stats = [
    {
      number: '15+',
      label: 'Years of Experience',
      icon: <Award className="w-8 h-8 text-green-600" />,
    },
    {
      number: '500+',
      label: 'Happy Clients',
      icon: <Users className="w-8 h-8 text-green-600" />,
    },
    {
      number: '4.9/5',
      label: 'Customer Rating',
      icon: <Star className="w-8 h-8 text-green-600" />,
    },
  ]

  const certifications = [
    'Licensed Landscape Contractor',
    'Certified Arborist (ISA)',
    'Irrigation Association Member',
    'EPA Certified Applicator',
  ]

  const testimonial = {
    quote:
      'Gardenly Care transformed our backyard into a stunning oasis. The team was professional, creative, and a pleasure to work with. Highly recommended!',
    author: 'Jane Doe',
    title: 'Happy Client',
    avatar:
      'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&q=80&fit=crop',
  }

  return (
    <section id="about-2" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image and Testimonial Section */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <ImageWithFallback
                src={"https://images.unsplash.com/photo-1723031749781-3ae099aa70ac?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8WW91ciUyMFBhcnRuZXIlMjBmb3IlMjBFeGNlcHRpb25hbCUyME91dGRvb3IlMjBTcGFjZXN8ZW58MHx8MHx8fDA%3D"}
                alt={'s'}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
            </div>

            <motion.div
              className="absolute -bottom-10 right-0 left-0 lg:left-auto lg:-right-10 w-11/12 lg:w-4/5 mx-auto bg-white rounded-xl shadow-2xl p-6 border border-gray-100"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              <Quote className="w-8 h-8 text-green-500 mb-4" />
              <p className="text-gray-600 italic mb-4">
                "{testimonial.quote}"
              </p>
              <div className="flex items-center space-x-4">
                <img
                  src={testimonial.avatar}
                  alt={testimonial.author}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <h5 className="font-semibold text-gray-900">
                    {testimonial.author}
                  </h5>
                  <p className="text-sm text-gray-500">
                    {testimonial.title}
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Content and Stats Section */}
          <motion.div
            className="mt-20 lg:mt-0"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Badge className="mb-4 bg-green-100 text-green-800 border-green-200">
              <Leaf className="w-4 h-4 mr-2" />
              Who We Are
            </Badge>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Your Partner for Exceptional Outdoor Spaces
            </h2>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              At Gardenly Care, we are passionate about bringing your landscape visions to life. With a foundation built on over a decade of experience, we combine artistry with expertise to create beautiful, sustainable, and functional outdoor environments.
            </p>

            {/* Certifications and Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              {/* Stats */}
              <Card className="p-6 h-full border-0 shadow-md bg-white">
                <CardContent className="p-0">
                  <h4 className="font-semibold text-gray-900 mb-4">Our Achievements</h4>
                  <div className="space-y-4">
                    {stats.map((stat, index) => (
                      <div key={index} className="flex items-center space-x-4">
                        {stat.icon}
                        <div>
                          <p className="text-xl font-bold text-gray-900">{stat.number}</p>
                          <p className="text-sm text-gray-600">{stat.label}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Certifications */}
              <Card className="p-6 h-full border-0 shadow-md bg-white">
                <CardContent className="p-0">
                  <h4 className="font-semibold text-gray-900 mb-4">
                    Certifications & Memberships
                  </h4>
                  <div className="space-y-3">
                    {certifications.map((cert, index) => (
                      <motion.div
                        key={index}
                        className="flex items-center"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
                        <span className="text-gray-600 text-sm">
                          {cert}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => {
                  navigate('/contact')
                }}
              >
                Get a Free Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
